import {
  button,
  div,
  span,
  input,
  select,
  option,
  fnstate
} from '@srfnstack/fntags'

const titleCase = (s) => String(s || '').replace(/\b\w/g, c => c.toUpperCase())

// Pinterest ranks boards on how shoppers SEARCH, and boards need volume to rank
// at all — so a board is "{searchable theme} {product noun}" ("Cottagecore Wall
// Art"), never an audience label ("cottagecore witches") and never one design.
const productNoun = (productType) => {
  const t = String(productType || '').toLowerCase()
  if (t.includes('canvas') || t.includes('wall') || t.includes('poster')) return 'wall art'
  if (t.includes('shirt') || t.includes('tee')) return 't-shirts'
  if (t.includes('hoodie') || t.includes('sweat')) return 'sweatshirts'
  return 'picks'
}

// board_group (many tribes → one searchable theme) is the preferred source;
// the tribe itself is the fallback so a brand-new tribe still pins somewhere
// sensible, and the product noun alone is the floor.
const boardSuggestion = (p) => {
  const theme = p.board_group || p.tribe || (p.attributes && p.attributes.vibe) || ''
  const noun = productNoun(p.product_type)
  return String(theme).trim() ? `${String(theme).trim()} ${noun}` : noun
}

// Self-contained "Pin to Pinterest" control: a button that opens a board-picker
// modal, lists the connected account's boards (pre-selecting one whose name
// matches the product's tribe, to nudge toward the board-per-tribe model), and
// creates a pin that links back to the Etsy listing.
export const PinToPinterest = ({ product }) => {
  const boards = fnstate([], b => b.id)
  const selectedBoard = fnstate('')
  const loading = fnstate(false)
  const pinning = fnstate(false)
  const open = fnstate(false)
  const error = fnstate('')
  const pinUrl = fnstate('')
  const newBoardName = fnstate('')
  const creating = fnstate(false)

  const openPicker = async () => {
    error('')
    pinUrl('')
    open(true)
    if (boards().length) return
    loading(true)
    try {
      const res = await fetch('/api/system/integrations/pinterest-boards')
      const d = await res.json().catch(() => ({}))
      if (res.ok) {
        const list = d.boards || []
        const p = product() || {}
        // Boards are per TRIBE (audience), never per design — a board named after
        // one design ("Sleeping Fox") can only ever hold that design's pins.
        // Fall back to the vibe, then to a product-type bucket; never the title.
        const suggestion = boardSuggestion(p)
        const key = suggestion ? String(suggestion).toLowerCase() : ''
        const match = key ? list.find(b => b.name && b.name.toLowerCase().includes(key)) : null
        selectedBoard(match ? match.id : (list[0] ? list[0].id : ''))
        boards(list)
        // Suggest a board name for the "new board" field (board-per-tribe).
        if (suggestion && !match) newBoardName(titleCase(String(suggestion).trim()))
      } else {
        error(d.error === 'Pinterest is not connected' ? 'Connect Pinterest in Settings first.' : (d.error || 'Failed to load boards'))
      }
    } catch (e) { error(e.message) }
    loading(false)
  }

  const createBoard = async () => {
    const name = newBoardName().trim()
    if (!name) { error('Enter a board name.'); return }
    creating(true)
    error('')
    try {
      const res = await fetch('/api/system/integrations/pinterest-create-board', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      })
      const d = await res.json().catch(() => ({}))
      if (res.ok && d.success && d.board) {
        boards([...boards(), d.board])
        selectedBoard(d.board.id)
        newBoardName('')
      } else {
        error(d.detail || d.error || 'Failed to create board')
      }
    } catch (e) { error(e.message) }
    creating(false)
  }

  const doPin = async () => {
    if (!selectedBoard()) { error('Pick a board first.'); return }
    pinning(true)
    error('')
    pinUrl('')
    try {
      const res = await fetch(`/api/product-concepts/${product().id}/pin-to-pinterest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board_id: selectedBoard() })
      })
      const d = await res.json().catch(() => ({}))
      if (res.ok && d.success) pinUrl(d.pinUrl || 'ok')
      else error(d.detail || d.error || 'Failed to create pin')
    } catch (e) { error(e.message) }
    pinning(false)
  }

  return div({ class: 'inline-flex' },
    button({
      class: 'btn btn-outline flex-1 md:flex-none',
      style: 'border-color:#e60023;color:#e60023',
      onclick: openPicker
    }, span({ class: 'flex items-center gap-2' },
      span({ class: 'material-symbols-rounded text-base' }, 'push_pin'), 'Pin to Pinterest')),
    open.bindAs(o => o
      ? div({ class: 'modal modal-open' },
        div({ class: 'modal-box' },
          div({ class: 'font-bold text-lg mb-3' }, 'Pin to Pinterest'),
          loading.bindAs(l => l ? div({ class: 'py-2' }, span({ class: 'loading loading-spinner' })) : span()),
          error.bindAs(e => e ? div({ class: 'alert alert-error text-sm mb-3' }, span(e)) : span()),
          pinUrl.bindAs(u => u
            ? div({ class: 'alert alert-success text-sm mb-3' },
              span('Pinned! '),
              u !== 'ok' ? button({ class: 'btn btn-xs ml-2', onclick: () => window.open(u) }, 'View pin') : span())
            : span()),
          div({ class: 'form-control mb-4' },
            boards.bindChildren(select({
              class: 'select select-bordered w-full',
              value: selectedBoard.bindAttr(),
              onchange: (e) => selectedBoard(e.target.value)
            }), (bState) => {
              const b = bState()
              return option({ value: b.id, selected: selectedBoard() === b.id }, b.name)
            })
          ),
          div({ class: 'divider text-xs opacity-60 my-1' }, 'or create a new board'),
          div({ class: 'flex gap-2 mb-4' },
            input({
              class: 'input input-bordered input-sm flex-1',
              placeholder: 'New board name (e.g. tribe)',
              value: newBoardName.bindAttr(),
              oninput: (e) => newBoardName(e.target.value)
            }),
            button({
              class: 'btn btn-sm btn-outline',
              disabled: creating.bindAttr(c => c || undefined),
              onclick: createBoard
            }, creating.bindAs(c => c ? span({ class: 'loading loading-spinner loading-xs' }) : 'Create board'))
          ),
          div({ class: 'modal-action' },
            button({ class: 'btn', onclick: () => open(false) }, 'Close'),
            button({
              class: 'btn btn-primary',
              disabled: pinning.bindAttr(pv => pv || undefined),
              onclick: doPin
            }, pinning.bindAs(pv => pv ? span({ class: 'loading loading-spinner loading-xs' }) : 'Create Pin'))
          )
        ))
      : span())
  )
}
